import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { getAuth, deleteUser } from 'firebase/auth';

const UserManagement = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openForm, setOpenForm] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
  });
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [error, setError] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    role: 'user',
    disabled: false,
    phoneNumber: '',
  });

  const auth = getAuth();

  // Load users data
  const loadUsers = async (reset = false) => {
    try {
      setLoading(reset);
      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      let usersQuery = collection(db, 'users');
      let constraints = [];

      if (roleFilter !== 'all') {
        constraints.push(where('role', '==', roleFilter));
      }

      if (statusFilter !== 'all') {
        const isDisabled = statusFilter === 'disabled';
        constraints.push(where('disabled', '==', isDisabled));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(10));

      if (lastVisible && !reset) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(usersQuery, ...constraints);
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setHasMore(false);
        setLoadingMore(false);
        setLoading(false);
        return;
      }

      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(newLastVisible);

      const newUsers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        lastLogin: doc.data().lastLogin?.toDate()
      }));

      if (reset) {
        setUsers(newUsers);
      } else {
        setUsers(prev => [...prev, ...newUsers]);
      }

      setHasMore(querySnapshot.docs.length === 10);
    } catch (error) {
      console.error('加载用户数据失败:', error);
      setSnackbar({
        open: true,
        message: '加载用户数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    loadUsers(true);
  }, [roleFilter, statusFilter]);

  // Handle search
  const handleSearch = () => {
    if (searchTerm.trim() !== '') {
      const filtered = users.filter(
        user => 
          (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.id && user.id.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setUsers(filtered);
      setHasMore(false);
    } else {
      loadUsers(true);
    }
  };

  // View user details
  const handleViewUser = async (id) => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', id));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setSelectedUserDetails(userData);
        setSelectedUser(id);
        setOpenDetailsDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: '找不到用户数据',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('加载用户数据失败:', error);
      setSnackbar({
        open: true,
        message: '加载用户数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit user
  const handleEditUser = async (id) => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', id));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFormData({
          displayName: userData.displayName || '',
          email: userData.email || '',
          role: userData.role || 'user',
          disabled: userData.disabled || false,
          phoneNumber: userData.phoneNumber || '',
        });
        setSelectedUser(id);
        setOpenForm(true);
      } else {
        setSnackbar({
          open: true,
          message: '找不到用户数据',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('加载用户数据失败:', error);
      setSnackbar({
        open: true,
        message: '加载用户数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle switch change
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      await updateDoc(doc(db, 'users', selectedUser), {
        displayName: formData.displayName,
        role: formData.role,
        disabled: formData.disabled,
        phoneNumber: formData.phoneNumber,
        updatedAt: serverTimestamp(),
      });
      
      // Update local state
      setUsers(users.map(user => {
        if (user.id === selectedUser) {
          return {
            ...user,
            displayName: formData.displayName,
            role: formData.role,
            disabled: formData.disabled,
            phoneNumber: formData.phoneNumber,
          };
        }
        return user;
      }));
      
      setSnackbar({
        open: true,
        message: '用户信息已更新',
        severity: 'success',
      });
      
      setOpenForm(false);
    } catch (error) {
      console.error('更新用户数据失败:', error);
      setSnackbar({
        open: true,
        message: '更新用户数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle user role
  const handleToggleRole = async (id, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    
    setConfirmDialog({
      open: true,
      title: `确认更改用户角色`,
      message: `确定要将此用户角色从 "${currentRole === 'admin' ? '管理员' : '用户'}" 更改为 "${newRole === 'admin' ? '管理员' : '用户'}" 吗？`,
      action: async () => {
        try {
          setLoading(true);
          await updateDoc(doc(db, 'users', id), {
            role: newRole,
            updatedAt: serverTimestamp(),
          });
          
          // Update local state
          setUsers(users.map(user => {
            if (user.id === id) {
              return {
                ...user,
                role: newRole,
              };
            }
            return user;
          }));
          
          setSnackbar({
            open: true,
            message: `用户角色已更新为 ${newRole === 'admin' ? '管理员' : '用户'}`,
            severity: 'success',
          });
        } catch (error) {
          console.error('更新用户角色失败:', error);
          setSnackbar({
            open: true,
            message: '更新用户角色失败: ' + error.message,
            severity: 'error',
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Toggle user status (enable/disable)
  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    
    setConfirmDialog({
      open: true,
      title: `确认${newStatus ? '禁用' : '启用'}用户`,
      message: `确定要${newStatus ? '禁用' : '启用'}此用户吗？${newStatus ? '禁用后用户将无法登录。' : ''}`,
      action: async () => {
        try {
          setLoading(true);
          await updateDoc(doc(db, 'users', id), {
            disabled: newStatus,
            updatedAt: serverTimestamp(),
          });
          
          // Update local state
          setUsers(users.map(user => {
            if (user.id === id) {
              return {
                ...user,
                disabled: newStatus,
              };
            }
            return user;
          }));
          
          setSnackbar({
            open: true,
            message: `用户已${newStatus ? '禁用' : '启用'}`,
            severity: 'success',
          });
        } catch (error) {
          console.error(`${newStatus ? '禁用' : '启用'}用户失败:`, error);
          setSnackbar({
            open: true,
            message: `${newStatus ? '禁用' : '启用'}用户失败: ` + error.message,
            severity: 'error',
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Handle tab change in user detail view
  const handleTabChange = (event, newValue) => {
    setCurrentTabIndex(newValue);
  };

  // Format date from Firebase timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('zh-CN');
    }
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // Handle delete
  const handleDelete = async (userId) => {
    if (window.confirm('确定要删除这个用户吗？此操作不可撤销。')) {
      try {
        // 删除用户文档
        await deleteDoc(doc(db, 'users', userId));
        
        // 如果可能，也删除 Authentication 中的用户
        try {
          const user = auth.currentUser;
          if (user && user.uid === userId) {
            await deleteUser(user);
          }
        } catch (authErr) {
          console.error('删除认证用户失败:', authErr);
        }

        loadUsers(true);
      } catch (err) {
        console.error('删除用户失败:', err);
        setError('删除用户失败');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="搜索用户"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>角色</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label="角色"
            >
              <MenuItem value="all">全部角色</MenuItem>
              <MenuItem value="admin">管理员</MenuItem>
              <MenuItem value="user">普通用户</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状态</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="状态"
            >
              <MenuItem value="all">全部状态</MenuItem>
              <MenuItem value="active">活跃</MenuItem>
              <MenuItem value="disabled">已禁用</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadUsers(true)}
            color="primary"
            variant="outlined"
            size="medium"
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* Users table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="用户列表">
          <TableHead>
            <TableRow>
              <TableCell>用户</TableCell>
              <TableCell>电子邮件</TableCell>
              <TableCell>角色</TableCell>
              <TableCell align="center">状态</TableCell>
              <TableCell>注册时间</TableCell>
              <TableCell>最后登录</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  暂无数据
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={user.photoURL} 
                        sx={{ mr: 2 }}
                      >
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      <Typography variant="body2">
                        {user.displayName || '未设置昵称'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                      label={user.role === 'admin' ? '管理员' : '用户'}
                      color={user.role === 'admin' ? 'primary' : 'default'}
                      variant={user.role === 'admin' ? 'filled' : 'outlined'}
                      size="small"
                      onClick={() => handleToggleRole(user.id, user.role)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={user.disabled ? <BlockIcon /> : <CheckCircleIcon />}
                      label={user.disabled ? '已禁用' : '活跃'}
                      color={user.disabled ? 'error' : 'success'}
                      size="small"
                      onClick={() => handleToggleStatus(user.id, user.disabled)}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(user.createdAt)}
                  </TableCell>
                  <TableCell>
                    {formatDate(user.lastLogin)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="查看详情">
                        <IconButton
                          color="info"
                          onClick={() => handleViewUser(user.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="编辑用户">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditUser(user.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除用户">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(user.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load more button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={() => loadUsers(false)}
            disabled={loadingMore}
            variant="outlined"
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore ? '加载中...' : '加载更多'}
          </Button>
        </Box>
      )}

      {/* Edit user form dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>编辑用户</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="显示名称"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="电子邮件"
                name="email"
                value={formData.email}
                disabled
                margin="normal"
                helperText="电子邮件不可更改"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>角色</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  label="角色"
                >
                  <MenuItem value="user">用户</MenuItem>
                  <MenuItem value="admin">管理员</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.disabled}
                    onChange={handleSwitchChange}
                    name="disabled"
                  />
                }
                label="禁用用户"
              />
              {formData.disabled && (
                <Typography variant="caption" color="error">
                  用户被禁用后将无法登录
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="电话号码"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>取消</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* User details dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>用户详情</DialogTitle>
        <DialogContent dividers>
          {selectedUserDetails && (
            <Box>
              {/* User header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  src={selectedUserDetails.photoURL} 
                  sx={{ width: 80, height: 80, mr: 3 }}
                >
                  {selectedUserDetails.displayName ? selectedUserDetails.displayName.charAt(0).toUpperCase() : '?'}
                </Avatar>
                <Box>
                  <Typography variant="h5">
                    {selectedUserDetails.displayName || '未设置昵称'}
                  </Typography>
                  <Typography variant="body1" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {selectedUserDetails.email}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Chip
                      icon={selectedUserDetails.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                      label={selectedUserDetails.role === 'admin' ? '管理员' : '用户'}
                      color={selectedUserDetails.role === 'admin' ? 'primary' : 'default'}
                      size="small"
                    />
                    <Chip
                      icon={selectedUserDetails.disabled ? <BlockIcon /> : <CheckCircleIcon />}
                      label={selectedUserDetails.disabled ? '已禁用' : '活跃'}
                      color={selectedUserDetails.disabled ? 'error' : 'success'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Tabs navigation */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={currentTabIndex} onChange={handleTabChange}>
                  <Tab label="基本信息" />
                  <Tab label="订单历史" />
                  <Tab label="活动日志" />
                </Tabs>
              </Box>

              {/* Basic info tab */}
              {currentTabIndex === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          账户信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense disablePadding>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="用户ID" 
                              secondary={selectedUser} 
                            />
                          </ListItem>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="注册时间" 
                              secondary={formatDate(selectedUserDetails.createdAt)} 
                            />
                          </ListItem>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="最后登录" 
                              secondary={formatDate(selectedUserDetails.lastLogin)} 
                            />
                          </ListItem>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="验证状态" 
                              secondary={
                                <Chip
                                  size="small"
                                  icon={selectedUserDetails.emailVerified ? <CheckCircleIcon /> : <CancelIcon />}
                                  label={selectedUserDetails.emailVerified ? '已验证' : '未验证'}
                                  color={selectedUserDetails.emailVerified ? 'success' : 'warning'}
                                />
                              } 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          个人资料
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense disablePadding>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="姓名" 
                              secondary={selectedUserDetails.displayName || 'N/A'} 
                            />
                          </ListItem>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemText 
                              primary="电话" 
                              secondary={selectedUserDetails.phoneNumber || 'N/A'} 
                            />
                          </ListItem>
                          {selectedUserDetails.profile && (
                            <>
                              <ListItem disablePadding sx={{ py: 1 }}>
                                <ListItemText 
                                  primary="性别" 
                                  secondary={selectedUserDetails.profile.gender || 'N/A'} 
                                />
                              </ListItem>
                              <ListItem disablePadding sx={{ py: 1 }}>
                                <ListItemText 
                                  primary="出生日期" 
                                  secondary={selectedUserDetails.profile.birthdate || 'N/A'} 
                                />
                              </ListItem>
                            </>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  {selectedUserDetails.address && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            地址信息
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">
                                收件人: {selectedUserDetails.address.name || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">
                                电话: {selectedUserDetails.address.phone || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary">
                                地址: {
                                  [
                                    selectedUserDetails.address.province,
                                    selectedUserDetails.address.city,
                                    selectedUserDetails.address.district,
                                    selectedUserDetails.address.address
                                  ].filter(Boolean).join(', ') || 'N/A'
                                }
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary">
                                邮编: {selectedUserDetails.address.postalCode || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {selectedUserDetails.settings && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            用户设置
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <List dense disablePadding>
                            <ListItem disablePadding sx={{ py: 1 }}>
                              <ListItemText 
                                primary="接收邮件通知" 
                                secondary={selectedUserDetails.settings.emailNotifications ? '是' : '否'} 
                              />
                            </ListItem>
                            <ListItem disablePadding sx={{ py: 1 }}>
                              <ListItemText 
                                primary="默认语言" 
                                secondary={selectedUserDetails.settings.language || '简体中文'} 
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Orders history tab */}
              {currentTabIndex === 1 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    订单历史记录将在此显示。该功能尚未实现。
                  </Typography>
                </Box>
              )}

              {/* Activity log tab */}
              {currentTabIndex === 2 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    用户活动日志将在此显示。该功能尚未实现。
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>关闭</Button>
          <Button
            onClick={() => {
              setOpenDetailsDialog(false);
              handleEditUser(selectedUser);
            }}
            color="primary"
            variant="contained"
          >
            编辑用户
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            取消
          </Button>
          <Button
            onClick={() => {
              confirmDialog.action();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;